import type {
  DataIntegrityIssueType,
  BillingStatus,
  SerializedCustomer,
  SerializedSupplyPoint,
  SerializedMeter,
  SerializedCustomEmissionFactor,
  Period as PeriodEnum,
  Granularity as GranularityEnum,
  Previous as PreviousEnum,
  AnalysisCalculation as AnalysisCalculationEnum,
  AuditLogAction as AuditLogActionEnum,
  Collection as CollectionEnum,
  UserRole as UserRoleEnum,
  SplitBy as SplitByEnum,
  Source as SourceEnum,
  ScopeAndStream as ScopeAndStreamEnum,
  MeterPlacement,
  MeterStatusCode,
  SerializedLease,
  SerializedPopulatedEstablishment,
  SerializedFloorPlan,
  SerializedTenant,
} from '@enmon/commons/lib/enmon';
import type { Commodity as CommodityEnum } from '@enmon/commons/lib/shared';
import type {
  EmissionScope as EmissionScopeEnum,
  EmissionStream as EmissionStreamEnum,
} from '@enmon/commons/lib/shared/emissions';
import type { ManualRecordStatus } from '@enmon/commons/lib/enmon/records/manual/status';
import type { RemoteReadingType as RemoteReadingTypeEnum } from '@/routes/records/routes/sensor/enums/RemoteReadingType';
import type { DateRangePickerValue } from '../form';
import type { Sorter } from '../pagination';
import type { SupplyPointCategoryType } from '@enmon/commons/lib/enmon';
import type { SupplyPointStatus } from '@enmon/commons/lib/enmon';

/**
 * Enum of all possible Page Filter Fields.
 * is used to create PAGE_FIELDS
 */
export enum FilterField {
  Search = 'search',
  Commodities = 'commodities',
  // In the future filter field will be customers and it will be multiselect
  Customer = 'customer',
  Tags = 'tags',
  Establishments = 'establishments',
  SupplyPoints = 'supplyPoints',
  Meters = 'meters',
  MeterRegisters = 'meterRegisters',
  SupplyPointCategories = 'supplyPointCategories',
  SupplyPointStatuses = 'supplyPointStatuses',
  FloorPlans = 'floorPlans',
  Tenants = 'tenants',
  LeaseUnits = 'leaseUnits',
  EmissionFactorCategories = 'emissionFactorCategories',
  EmissionFactors = 'emissionFactors',
  EmissionScope = 'emissionScope',
  EmissionStream = 'emissionStream',
  CustomerEmissionFactors = 'customerEmissionFactors',
  Period = 'period',
  Previous = 'previous',
  Placements = 'placements',
  Sources = 'sources',
  IntegrityIssueTypes = 'integrityIssueTypes',
  Statuses = 'statuses',
  UserType = 'userType',
  Action = 'action',
  Collections = 'collections',
  Granularity = 'granularity',
  Calculation = 'calculation',
  Date = 'date',
  ThirdParties = 'thirdParties',
  /**
   * @deprecated Use useToolbarState hook instead.
   */
  Split = 'split',
  /**
   * @deprecated Use usePagination hook instead.
   */
  Sort = 'sort',
  Sorts = 'sorts',
  IsCorrection = 'isCorrection',
  ManualRecordStatuses = 'manualRecordStatuses',
  RemoteReadingType = 'remoteReadingType',
  ScopeAndStream = 'scopeAndStream',
  BillingStatuses = 'billingStatuses',
}

export interface BackendPeriodShape {
  period?: PeriodEnum;
  from?: Date;
  to?: Date;
}

export type PeriodShape = DateRangePickerValue;

/**
 * Complete type of the whole filter store object.
 *
 * TODO: fields with plural names should be just array (https://enmontech.atlassian.net/browse/ENMON-1305)
 */
export interface Filter {
  [FilterField.Search]?: string;
  [FilterField.Commodities]?: CommodityEnum | CommodityEnum[];
  [FilterField.Customer]?: SerializedCustomer['_id'];
  [FilterField.Tags]?: string[];
  [FilterField.Establishments]?: SerializedPopulatedEstablishment['_id'] | SerializedPopulatedEstablishment['_id'][];
  [FilterField.SupplyPoints]?: SerializedSupplyPoint['_id'] | SerializedSupplyPoint['_id'][];
  [FilterField.Meters]?: SerializedMeter['_id'] | SerializedMeter['_id'][];
  [FilterField.MeterRegisters]?: string[];
  [FilterField.SupplyPointCategories]?: SupplyPointCategoryType[];
  [FilterField.SupplyPointStatuses]?: SupplyPointStatus[];
  [FilterField.FloorPlans]?: SerializedFloorPlan['_id'][];
  [FilterField.Tenants]?: SerializedTenant['_id'][];
  [FilterField.LeaseUnits]?: SerializedLease['_id'][];
  [FilterField.Date]?: Date;
  [FilterField.Period]?: PeriodShape;
  [FilterField.Placements]?: MeterPlacement[];
  [FilterField.Granularity]?: GranularityEnum;
  [FilterField.Previous]?: PreviousEnum;
  [FilterField.EmissionFactorCategories]?: number | number[];
  [FilterField.EmissionScope]?: EmissionScopeEnum;
  [FilterField.EmissionFactors]?: string | string[];
  [FilterField.EmissionStream]?: EmissionStreamEnum;
  [FilterField.CustomerEmissionFactors]?:
    | SerializedCustomEmissionFactor['_id']
    | SerializedCustomEmissionFactor['_id'][];
  [FilterField.ScopeAndStream]?: ScopeAndStreamEnum;
  [FilterField.Action]?: AuditLogActionEnum;
  [FilterField.Sources]?: SourceEnum[];
  [FilterField.IntegrityIssueTypes]?: DataIntegrityIssueType[];
  [FilterField.Collections]?: CollectionEnum[];
  [FilterField.UserType]?: UserRoleEnum;
  [FilterField.Calculation]?: AnalysisCalculationEnum;
  [FilterField.Statuses]?: MeterStatusCode[];
  [FilterField.ThirdParties]?: string[];
  [FilterField.ManualRecordStatuses]?: ManualRecordStatus[];
  [FilterField.RemoteReadingType]?: RemoteReadingTypeEnum;
  [FilterField.IsCorrection]?: boolean[];
  [FilterField.BillingStatuses]?: BillingStatus[];
  /**
   * @deprecated Use usePagination hook instead.
   */
  [FilterField.Sort]?: string;
  /**
   * @deprecated Use usePagination hook instead.
   */
  [FilterField.Sorts]?: Sorter[];
  /**
   * @deprecated Use useToolbarState hook instead.
   */
  [FilterField.Split]?: SplitByEnum[] | SplitByEnum | null;
}

export const COMMON_FILTER_FIELDS = [
  FilterField.Customer,
  FilterField.Tags,
  FilterField.Establishments,
  FilterField.SupplyPoints,
  FilterField.Meters,
  FilterField.Period,
  FilterField.Granularity,
] as const;

export type CommonFilterField = (typeof COMMON_FILTER_FIELDS)[number];

export const isCommonFilterField = (value: unknown): value is (typeof COMMON_FILTER_FIELDS)[number] => {
  return COMMON_FILTER_FIELDS.some(commonField => typeof commonField === typeof value && commonField === value);
};
